import React from "react";
import ColorPicker from "../ColorPicker";
import Textarea from "../inputs/Textarea";
import TextInput from "../inputs/TextInput";
import SelectInput from "../inputs/SelectInput";
import SpacingInput from "../inputs/SpacingInput";
import AlignmentInput from "../inputs/AlignmentInput";

interface TextPropertyFieldProps {
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  style?: any;
  link?: string;
  onStyleChange: (styleProperty: string, styleValue: string) => void;
}

const toEditableString = (input: any, context: string = "unknown"): string => {
  if (typeof input === "string") return input;
  if (input === null || input === undefined) return "";
  if (typeof input === "number" || typeof input === "boolean")
    return String(input);

  // Compatibilidade com payload legado em formato objeto (ex.: { value: "..." })
  if (typeof input === "object" && typeof input.value === "string") {
    return input.value;
  }

  // Diagnóstico para rastrear payloads fora do schema esperado.
  if (typeof input === "object") {
    const keys = Object.keys(input || {});
    console.warn(
      `[TextPropertyField] Valor não-texto recebido em "${context}". Tipo=object, chaves=${keys.join(",")}`,
      input,
    );
  } else {
    console.warn(
      `[TextPropertyField] Valor não-texto recebido em "${context}". Tipo=${typeof input}`,
      input,
    );
  }

  return "";
};

// Função para converter string de estilo em objeto
const parseStyleString = (styleString: any): Record<string, string> => {
  if (!styleString || typeof styleString !== 'string') return {};

  const styles: Record<string, string> = {};
  styleString.split(";").forEach((rule) => {
    const [property, value] = rule.split(":").map((s) => s.trim());
    if (property && value) {
      styles[property] = value;
    }
  });
  return styles;
};

// Função para converter objeto de estilo em string
const buildStyleString = (styles: Record<string, string>): string => {
  return Object.entries(styles)
    .filter(([_, value]) => value !== undefined && value !== "")
    .map(([property, value]) => `${property}: ${value}`)
    .join("; ");
};

// Função para aplicar formatação com seleção de texto
const applyFormatting = (
  currentValue: string,
  tag: string,
  textareaRef: React.RefObject<HTMLTextAreaElement>
): string => {
  if (!textareaRef.current) return currentValue;

  const textarea = textareaRef.current;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = textarea.value.substring(start, end);

  if (selectedText) {
    // Se há texto selecionado, envolve com a tag
    const beforeSelection = currentValue.substring(0, start);
    const afterSelection = currentValue.substring(end);

    return (
      beforeSelection + `<${tag}>${selectedText}</${tag}>` + afterSelection
    );
  } else {
    // Se não há seleção, adiciona tags no final
    return currentValue + `<${tag}></${tag}>`;
  }
};

export default function TextPropertyField({
  value,
  onChange,
  placeholder,
  style,
  link,
  onStyleChange,
}: TextPropertyFieldProps) {
  const [localValue, setLocalValue] = React.useState(
    toEditableString(value, "initial-state"),
  );
  const [localLink, setLocalLink] = React.useState(link || "");
  const [localStyleObj, setLocalStyleObj] = React.useState(
    parseStyleString(style || "")
  );
  const [isEditingMode, setIsEditingMode] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const contentEditableRef = React.useRef<HTMLDivElement>(null);

  // Função para ajustar altura do textarea automaticamente
  const adjustTextareaHeight = React.useCallback(() => {
    if (textareaRef.current) {
      // Reset height to auto para recalcular
      textareaRef.current.style.height = 'auto';
      // Ajustar altura baseado no scrollHeight, com mínimo de 200px
      const newHeight = Math.max(200, textareaRef.current.scrollHeight);
      textareaRef.current.style.height = `${newHeight}px`;
      // Garantir minHeight também
      textareaRef.current.style.minHeight = '200px';
    }
  }, []);

  React.useEffect(() => {
    setLocalValue(toEditableString(value, "effect-sync:value"));
    setLocalLink(link || "");
    setLocalStyleObj(parseStyleString(style || ""));
  }, [value, link, style]);

  // Ajustar altura do textarea quando o valor muda ou quando entra no modo de edição
  React.useEffect(() => {
    if (isEditingMode) {
      setTimeout(adjustTextareaHeight, 0);
    }
  }, [localValue, isEditingMode, adjustTextareaHeight]);

  // Sincronizar conteúdo do contentEditable quando muda o valor (apenas quando não está editando)
  React.useEffect(() => {
    if (contentEditableRef.current && !isEditingMode) {
      // Só atualizar se o conteúdo for diferente para evitar perder o foco
      const currentContent = contentEditableRef.current.innerHTML;
      let newContent = localValue || '';
      // Normalizar divs vazios para <br /> ao carregar
      newContent = normalizeHtmlContent(newContent);
      if (currentContent !== newContent) {
        contentEditableRef.current.innerHTML = newContent;
      }
      // Atualizar classe para mostrar/esconder placeholder baseado no texto real
      const textContent = contentEditableRef.current.textContent || '';
      const isEmpty = !textContent.trim();
      if (isEmpty) {
        contentEditableRef.current.classList.add('empty');
      } else {
        contentEditableRef.current.classList.remove('empty');
      }
    }
  }, [localValue, isEditingMode]);

  // Inicializar placeholder quando componente monta no modo visual
  React.useEffect(() => {
    if (contentEditableRef.current && !isEditingMode) {
      const textContent = contentEditableRef.current.textContent || '';
      const isEmpty = !textContent.trim();
      if (isEmpty) {
        contentEditableRef.current.classList.add('empty');
      } else {
        contentEditableRef.current.classList.remove('empty');
      }
    }
  }, [isEditingMode]);

  const handleValueChange = (newValue: string) => {
    setLocalValue(newValue);
    const styleString = buildStyleString(localStyleObj);
    const linkObject = localLink ? { value: localLink } : undefined;
    onChange({ value: newValue, style: styleString, link: linkObject });
    // Ajustar altura após mudança
    setTimeout(adjustTextareaHeight, 0);
  };

  // Função para converter divs vazios em <br /> ao carregar conteúdo
  const normalizeHtmlContent = (html: any): string => {
    const safeHtml = toEditableString(html, "normalizeHtmlContent");
    if (!safeHtml) return safeHtml;
    
    // Substituir divs vazios ou com apenas espaços por <br />
    return safeHtml
      .replace(/<div\s*><\/div>/gi, '<br />')
      .replace(/<div\s*>\s*<\/div>/gi, '<br />')
      .replace(/<div\s*><br\s*\/?><\/div>/gi, '<br />');
  };

  const handleContentEditableChange = () => {
    if (contentEditableRef.current) {
      let htmlContent = contentEditableRef.current.innerHTML;
      
      // Normalizar divs vazios para <br /> antes de salvar
      htmlContent = normalizeHtmlContent(htmlContent);
      
      handleValueChange(htmlContent);
      // Atualizar classe para mostrar/esconder placeholder
      // Remove tags HTML para verificar se há conteúdo real
      const textContent = contentEditableRef.current.textContent || '';
      const isEmpty = !textContent.trim();
      if (isEmpty) {
        contentEditableRef.current.classList.add('empty');
      } else {
        contentEditableRef.current.classList.remove('empty');
      }
    }
  };

  // Handler para interceptar Enter e inserir <br /> ao invés de criar div
  const handleContentEditableKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Criar e inserir <br />
        const br = document.createElement('br');
        range.deleteContents();
        range.insertNode(br);
        
        // Mover cursor para depois do <br />
        range.setStartAfter(br);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Atualizar conteúdo
        handleContentEditableChange();
      }
    }
  };

  const handleToggleMode = () => {
    if (isEditingMode && contentEditableRef.current) {
      // Saindo do modo edição: pegar HTML do contentEditable
      const htmlContent = contentEditableRef.current.innerHTML;
      setLocalValue(htmlContent);
      handleValueChange(htmlContent);
    } else if (!isEditingMode && contentEditableRef.current) {
      // Entrando no modo visual: inicializar conteúdo
      let content = localValue || '';
      // Normalizar divs vazios para <br /> ao carregar
      content = normalizeHtmlContent(content);
      contentEditableRef.current.innerHTML = content;
      // Atualizar classe para mostrar/esconder placeholder baseado no texto real
      const textContent = contentEditableRef.current.textContent || '';
      const isEmpty = !textContent.trim();
      if (isEmpty) {
        contentEditableRef.current.classList.add('empty');
      } else {
        contentEditableRef.current.classList.remove('empty');
      }
    }
    setIsEditingMode(!isEditingMode);
  };

  const handleLinkChange = (newLink: string) => {
    setLocalLink(newLink);
    const styleString = buildStyleString(localStyleObj);
    const linkObject = newLink ? { value: newLink } : undefined;
    onChange({ value: localValue, style: styleString, link: linkObject });
  };

  const handleStyleChangeLocal = (
    styleProperty: string,
    styleValue: string
  ) => {
    const newStyleObj = { ...localStyleObj, [styleProperty]: styleValue };
    setLocalStyleObj(newStyleObj);
    const styleString = buildStyleString(newStyleObj);
    const linkObject = localLink ? { value: localLink } : undefined;
    onChange({ value: localValue, style: styleString, link: linkObject });
    onStyleChange(styleProperty, styleValue);
  };

  const handleFormatting = (tag: string) => {
    if (isEditingMode) {
      // Modo HTML: usar textarea
      const newValue = applyFormatting(localValue, tag, textareaRef);
      handleValueChange(newValue);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    } else {
      // Modo visual: usar contentEditable
      if (contentEditableRef.current) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const selectedText = range.toString();
          
          if (selectedText) {
            // Há texto selecionado: envolver com tag
            const element = document.createElement(tag);
            try {
              range.surroundContents(element);
            } catch (e) {
              // Se surroundContents falhar, usar extractContents
              const contents = range.extractContents();
              element.appendChild(contents);
              range.insertNode(element);
            }
            selection.removeAllRanges();
            selection.addRange(range);
            handleContentEditableChange();
          } else {
            // Sem seleção: inserir tag vazia no cursor
            const element = document.createElement(tag);
            range.insertNode(element);
            range.setStartAfter(element);
            range.setEndAfter(element);
            selection.removeAllRanges();
            selection.addRange(range);
            handleContentEditableChange();
          }
          contentEditableRef.current.focus();
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-700">
            Conteúdo
          </label>
          <button
            type="button"
            onClick={handleToggleMode}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {isEditingMode ? 'Ver Visualização' : 'Editar HTML'}
          </button>
        </div>
        
        {isEditingMode ? (
          <Textarea
            ref={textareaRef}
            value={localValue}
            onChange={handleValueChange}
            rows={1}
            placeholder={placeholder}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs bg-white resize-none overflow-hidden"
          />
        ) : (
          <>
            <style>{`
              .content-editable-empty.empty:before {
                content: attr(data-placeholder);
                color: #9ca3af;
                pointer-events: none;
              }
              .content-editable-empty:focus.empty:before {
                opacity: 0.5;
              }
            `}</style>
            <div
              ref={contentEditableRef}
              contentEditable
              onBlur={handleContentEditableChange}
              onInput={handleContentEditableChange}
              onKeyDown={handleContentEditableKeyDown}
              onFocus={() => {
                if (contentEditableRef.current) {
                  contentEditableRef.current.classList.remove('empty');
                }
              }}
              className="content-editable-empty min-h-[200px] w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs bg-white"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
              data-placeholder={placeholder || 'Digite o conteúdo...'}
              suppressContentEditableWarning={true}
            />
          </>
        )}
        
        <div className="flex flex-wrap gap-2 mt-2">
          <button
            type="button"
            onClick={() => handleFormatting("strong")}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Bold
          </button>
          <button
            type="button"
            onClick={() => handleFormatting("em")}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Italic
          </button>
          <button
            type="button"
            onClick={() => handleFormatting("mark")}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Mark
          </button>
          <button
            type="button"
            onClick={() => handleFormatting("small")}
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
          >
            Small
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Link
        </label>
        <TextInput
          value={localLink}
          onChange={handleLinkChange}
          placeholder="Digite o link (ex: /categoria ou https://exemplo.com)"
        />
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Estilos</h4>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Tamanho da Fonte
          </label>
          <SelectInput
            value={localStyleObj["font-size"] || ""}
            onChange={(v) => handleStyleChangeLocal("font-size", v)}
            options={[
              { value: "", label: "Selecionar tamanho..." },
              { value: "12px", label: "Pequeno (12px)" },
              { value: "14px", label: "Normal (14px)" },
              { value: "16px", label: "Médio (16px)" },
              { value: "18px", label: "Grande (18px)" },
              { value: "20px", label: "Extra Grande (20px)" },
              { value: "24px", label: "Título (24px)" },
              { value: "32px", label: "Título Grande (32px)" },
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Peso da Fonte
          </label>
          <SelectInput
            value={localStyleObj["font-weight"] || ""}
            onChange={(v) => handleStyleChangeLocal("font-weight", v)}
            options={[
              { value: "", label: "Selecionar peso..." },
              { value: "300", label: "Light" },
              { value: "400", label: "Normal" },
              { value: "500", label: "Medium" },
              { value: "600", label: "Semi Bold" },
              { value: "700", label: "Bold" },
              { value: "900", label: "Black" },
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Cor do Texto
          </label>
          <ColorPicker
            color={localStyleObj["color"] || ""}
            onChange={(color) => handleStyleChangeLocal("color", color)}
          />
        </div>

                 <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Alinhamento
          </label>
          <AlignmentInput
            value={localStyleObj["text-align"] || "left"}
            onChange={(v) => handleStyleChangeLocal("text-align", v)}
          />
        </div>

                 <SpacingInput
          label="Padding"
          values={{
            top: localStyleObj["padding-top"] || "",
            right: localStyleObj["padding-right"] || "",
            bottom: localStyleObj["padding-bottom"] || "",
            left: localStyleObj["padding-left"] || ""
          }}
          onChange={(values) => {
            // Criar novo objeto de estilo
            const newStyleObj = { ...localStyleObj }
            
            // Atualizar ou remover valores de padding
            Object.entries(values).forEach(([side, value]) => {
              const property = `padding-${side}`
              if (value !== undefined && value !== "") {
                newStyleObj[property] = value as string
              } else {
                delete newStyleObj[property]
              }
            })
            
            // Atualizar estado e chamar onChange
            setLocalStyleObj(newStyleObj)
            const styleString = buildStyleString(newStyleObj)
            onChange({ value: localValue, style: styleString })
          }}
          unit="px"
        />

        <SpacingInput
          label="Margem"
          values={{
            top: localStyleObj["margin-top"] || "",
            right: localStyleObj["margin-right"] || "",
            bottom: localStyleObj["margin-bottom"] || "",
            left: localStyleObj["margin-left"] || ""
          }}
          onChange={(values) => {
            // Criar novo objeto de estilo
            const newStyleObj = { ...localStyleObj }
            
            // Atualizar ou remover valores de margin
            Object.entries(values).forEach(([side, value]) => {
              const property = `margin-${side}`
              if (value !== undefined && value !== "") {
                newStyleObj[property] = value as string
              } else {
                delete newStyleObj[property]
              }
            })
            
            // Atualizar estado e chamar onChange
            setLocalStyleObj(newStyleObj)
            const styleString = buildStyleString(newStyleObj)
            onChange({ value: localValue, style: styleString })
          }}
          unit="px"
        />
     </div>
    </div>
  );
}
